import { isOIDC } from 'client/applications/utils/ApplicationUtils';
import ApplicationApiV2 from 'client/api/v2/ApplicationApiV2';
import CollectionModule from 'client/store/modules/CollectionModule';
import SsoApi from 'client/api/v2/SsoApi';
import Vue from 'vue';

export const createApplicationCollection = (Api = ApplicationApiV2, id = '_id', displayLabel = 'displayLabel') => {
  const collection = new CollectionModule(Api, id, displayLabel);
  const applicationModule = collection.extractModule();

  const getInitialState = () => {
    const { state } = applicationModule;
    state.entities.itemDetails = {
      byId: {},
    };
    return state;
  };

  return {
    namespaced: true,

    state: getInitialState(),

    getInitialState,

    actions: {
      ...applicationModule.actions,

      /**
       * filterName is not guaranteed to be unique. Added `type` + `displayValue` for cases like:
       * password expiring within 7 vs. 14 days.
       * Both will use the same name 'password_expiration_date'
       */
      deleteFilter(context, {
        filterName,
        filterType = '',
        filterDisplayValue = '',
        reset = true,
      }) {
        if (reset) context.commit('RESET_COLLECTION', { soft: true });
        const params = (filterType && filterDisplayValue)
          ? { filterType, filterDisplayValue }
          : { filterName };
        context.commit('DELETE_FILTER', params);
      },

      /**
       * Re-implementing CollectionModule.deleteItems
       * To be able to include deletion of OIDC config(s)
       * Not using `async` package as it is severely out of date
       * AP version is 0.2.9, latest is 3.2.4
       */
      async deleteItems(context, ids = []) {
        const maxTasks = 5;
        const deletedIds = [];
        const failedTasks = [];
        const deleteItem = async (itemId) => {
          try {
            // For OIDC, delete hydra config first
            // Otherwise application won't be found in SI
            if (isOIDC(context.getters.getItem(itemId))) {
              await SsoApi.delete(itemId);
            }

            await ApplicationApiV2.delete(itemId);

            deletedIds.push(itemId);
          } catch (error) {
            failedTasks.push({ id: itemId, error });
          } finally {
            context.dispatch('ProgressModule/incrementCompleted', {}, { root: true });
          }
        };
        const taskBuckets = Array.from(
          // create task buckets, rounding up
          // Ex: 12 ids will create 3 buckets
          // 2 with 5 ids, and 1 with 2 ids
          { length: Math.ceil(ids.length / maxTasks) },
          (_, i) => ids
            // slice ids into buckets
            .slice(i * maxTasks, i * maxTasks + maxTasks)
            // map id bucket to Api call(s)
            .map(deleteItem),
        );

        context.dispatch('ProgressModule/updateBulkActionStatus', true, { root: true });
        context.dispatch('ProgressModule/setCounter', {
          completed: 0,
          total: ids.length,
        }, { root: true });

        await Promise.all(
          taskBuckets.map(async (taskBucket) => {
            await Promise.all(taskBucket);
          }),
        );

        context.commit('DELETE_ITEMS', deletedIds);

        context.dispatch('ProgressModule/updateBulkActionStatus', false, { root: true });
        context.dispatch('ProgressModule/setCounter', {
          completed: ids.length,
          total: ids.length,
        }, { root: true });

        return { deletedIds, failedTasks };
      },

      updateItemSelectedStatus(context, payload) {
        context.commit('UPDATE_ITEM_SELECTED_STATUS', payload);
      },
    },

    getters: {
      ...applicationModule.getters,
    },

    mutations: {
      ...applicationModule.mutations,

      DELETE_FILTER(state, { filterName, filterType = '', filterDisplayValue = '' }) {
        const filterByDisplayValue = () => (
          state.features.items.filters.filter(f => f.type !== filterType
            && f.displayValue !== filterDisplayValue)
        );
        const filterByName = () => state.features.items.filters.filter(f => f.name !== filterName);

        const newFilters = (filterType && filterDisplayValue)
          ? filterByDisplayValue() : filterByName();
        Vue.set(state.features.items, 'filters', newFilters);
      },

      SET_FILTER(state, { filter, pagination }) {
        const { filters } = state.features.items;
        // determine if there are existing filters of the same type that need to be replaced
        const existingFilters = filters.reduce((filterCollection, element, index) => {
          const filterExists = element.name === filter.name
            && element.displayValue !== filter.displayValue;
          if (filterExists) {
            filterCollection.push({ element, index });
          }
          return filterCollection;
        }, []);
        if (existingFilters.length > 0) {
          const newFilters = filters;
          // replace first filter and if there are more filters then convert those to bool
          existingFilters.forEach((existingFilter, index) => {
            if (index === 0) newFilters[existingFilter.index] = filter;
            else {
              newFilters[existingFilter.index] = false;
            }
          });
          // change filters to new filters array and remove bool elements
          Vue.set(state.features.items, 'filters', newFilters.filter(Boolean));
          return;
        }
        filters.push(filter);
        Vue.set(state.features.items, 'filters', filters);

        if (pagination) {
          const { limit } = pagination;
          Vue.set(state.features.items.pagination.limit, limit);
        }
      },

      UPDATE_ITEM_SELECTED_STATUS(state, { item, selected }) {
        Vue.set(
          state.entities.items.byId,
          item.id,
          {
            ...item,
            selected,
          },
        );
      },

    },
  };
};

export default createApplicationCollection();
